import React, { Dispatch, SetStateAction, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { ReactCalendarGroupRendererProps, ReactCalendarItemRendererProps, TimelineGroupBase } from "react-calendar-timeline";

import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import moment from "moment";

import {
  ModalProvider,
  areFieldOutcomesValidForYear,
  doRangesOverlap,
  generateRandomId,
  getCurrentReportingValues,
  isFieldEnrolled,
  isFieldWithoutPayoutDueToNoPoints,
  isNextYearOutcomeValid,
  outcomesExistOnField,
  timeIsWithinReportingPeriod,
  useAppType,
  useBulkDeleteFieldCropPractices,
  useCarbonProgramImpact,
  useFeatureFlag,
  useGetFarms,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticeTillages,
  useInView,
  useLocation,
  useNavigate,
  useTranslation,
} from "utils";

import { FEATURE_FLAGS, GET_FIELD_PRACTICES } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  AllCropsPracticeDataItem,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CarbonProgramResult,
  CategorizedFieldCropRecommendation,
  CropFCPO,
  CropItemData,
  FieldCropRecommendationsResponse,
  FieldListField,
  FieldPracticeResponse,
  GetVerificationEditingRequestsResponse,
  ItemsWithMissingData,
  ItemsWithMissingDataByFieldId,
  PRACTICE_SLUG,
  PRACTICE_TYPE,
  PracticeBundle,
  PracticeFCPO,
  SingleFieldOutcomes,
  TimelineCalendarItemsProps,
  VIEW_TYPE,
  VerificationEditingRequest,
} from "models";

import {
  BulkActionBar,
  CropRotationCard,
  Drawer,
  FieldCell,
  type FieldRowHighlight,
  Flex,
  Modal,
  MultiCropSelectionBar,
  Spin,
  TimelineCalendarWithFields,
  Typography,
  getTimelineRowHeight,
  Message as message,
} from "components";

import { AddEditCropDrawer } from "pages/app/all-field-view/components/AddEditCropDrawer";
import { BulkEditCrop, BulkEditCropDrawer } from "pages/app/all-field-view/components/BulkEditCropDrawer";
import { NoMatchesFeedback } from "pages/app/all-field-view/components/NoMatchesFeedback";
import { TimelineCropDetailsPanel } from "pages/app/all-field-view/components/TimelineCropDetailsPanel";
// import { moveResizeValidator } from "pages/app/field-timeline/utils/TimelineUtils";
import { CALENDAR_YEAR_END, CALENDAR_YEAR_START, NEW_CROP } from "pages/app/all-field-view/constants/cropForm";
import { MissingDataTarget, useAutoScrollToMissingData } from "pages/app/all-field-view/hooks/useAutoScrollToMissingData";
import { useMissingDataNavigation } from "pages/app/all-field-view/hooks/useMissingDataNavigation";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import {
  type BaselinePeriod,
  getAllCropsPracticeDataByYear,
  getCropOpenTasksCount,
  getFieldBaselinePeriod,
  getFieldOpenTasksCount,
  getFieldReportingPeriodHighlight,
  getItemsWithMissingDataSortedByStartTime,
  getVerificationEditingRequestsSnapshot,
  hasFieldLevelVerificationEditingRequests,
  hasItemsWithMissingData,
  isCheckboxClick,
  isSownInBaselinePeriod,
} from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { CropDetailsLine, getCropDetailsLines } from "pages/app/all-field-view/utils/CropDetailsUtils";
import { AddCropTypeDrawer } from "pages/app/all-fields-data-table-view/components/AddCropTypeDrawer";
import {
  buildMissingDataForm,
  getAddCropYearWindow,
  isMineralFertilizerOfferedByProgram,
} from "pages/app/all-fields-data-table-view/utils/AllFieldsDataTableUtils";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { getFieldBaselineYearRange } from "pages/app/field-timeline/utils/PracticeUtils";
import { FieldDetails } from "pages/app/fields/components/FieldDetails";
import { FieldEnrollmentDrawer } from "pages/app/fields/components/FieldEnrollmentDrawer";
import { YIELD_PSEUDO_PRACTICE_TYPE } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import { BulkDataDrawer } from "pages/app/historical-practice-data-table-view/components/BulkDataDrawer.tsx";
import {
  BULK_CROP_DATA_POINT,
  BULK_DATA_DRAWER_MODE,
  type BulkDataDrawerMode,
  type BulkDataPointType,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

type AllFieldsTimelineProps = {
  allFields: FieldListField[];
  filteredFields: FieldListField[];
  allFieldsPractices: Record<string, FieldPracticeResponse>;
  itemsWithMissingDataByFieldId: ItemsWithMissingDataByFieldId;
  timelineItems: TimelineCalendarItemsProps<CropItemData>[];
  carbonProgramOutcomesResponse?: CarbonProgramOutcomesResponse;
  isLoadingCarbonProgramOutcomes?: boolean;
  fieldsInView: string[];
  setFieldsInView: Dispatch<SetStateAction<string[]>>;
  cropRecommendationsByFieldId: FieldCropRecommendationsResponse | undefined;
  farmFieldsCount: number;
  enrolledFieldsCount: number;
  enrolledFieldsTotalHectares: number;
  isEnrollmentDataLoading?: boolean;
  fieldsSizeSummary: string | null;
  showBaselineYears: boolean;
  showCropDetails: boolean;
  hasAppliedFilters: boolean;
  reportingYear: number;
  isReportingYearLocked: boolean;
  shouldShowIndividualFieldPayouts: boolean;
  /** When true (Soil+3), field cells show minimum payout; otherwise average. */
  useMinimumPayoutForFieldCells?: boolean;
  verificationEditingRequests?: GetVerificationEditingRequestsResponse;
  handleClearFilters: () => void;
  onFieldNavigate?: () => void;
  navigateToFirstMissingData?: boolean;
  onNavigateToFirstMissingDataHandled?: () => void;
  onShowFieldUpload?: () => void;
};

export enum UPDATE_TYPE {
  START = "START",
  END = "END",
  MOVE = "MOVE",
}

const dayInMilliseconds = 1 * 24 * 60 * 60 * 1000;
type EnrollmentAndMissingItemsData = {
  itemsWithMissingData?: ItemsWithMissingData;
  isEnrolled: boolean;
  verificationEditingRequests: VerificationEditingRequest[];
};

export type FieldWithEnrollmentData = TimelineGroupBase & { data: FieldListField & EnrollmentAndMissingItemsData };

type GroupRendererProps = ReactCalendarGroupRendererProps<FieldWithEnrollmentData> & {
  carbonProgramOutcomes: CarbonProgramResult;
  cropRecommendations: CategorizedFieldCropRecommendation[];
  setFieldsInView: Dispatch<React.SetStateAction<string[]>>;
  onShowMissingDataYear: (fieldId: string, itemId: string) => void;
  carbonProgram: CarbonProgram | null;
  handleClearFilters: () => void;
  hasNoAllotment: boolean;
  fieldPayout: number | null;
  itemsWithMissingData: ItemsWithMissingData;
  reportingYear: number;
  shouldShowPayout: boolean;
  onFieldNavigate?: () => void;
  onOpenFieldDetails: (fieldId: string) => void;
  allFieldsPracticesByFieldId?: Record<string, FieldPracticeResponse>;
};

export type CropCardRefMap = Map<string, HTMLDivElement | null>;

const cropRenderer = ({
  carbonProgram,
  item: crop,
  getItemProps,
  itemContext,
  getCropCardRefMap,
  fields,
  cropCardRefCallback,
  selectedCropIds,
  canMultiSelectCrop,
  itemsWithMissingData,
  reportingYear,
  showBaselineYears,
  harvestBaselinePeriod,
  showCropDetails,
  getCropDetailsPanelLines,
  isSubmissionFeatureEnabled,
}: {
  carbonProgram?: CarbonProgram;
  item: TimelineCalendarItemsProps<CropItemData>;
  getItemProps: unknown;
  itemContext: unknown;
  getCropCardRefMap: () => CropCardRefMap;
  fields: FieldListField[];
  cropCardRefCallback: (getCropCardRefMap: () => CropCardRefMap) => (node: HTMLDivElement | null, fieldId: string, itemId: string) => void;
  selectedCropIds: string[];
  selectionConstraints: { cropType: CROP_TYPE_FCPO | null; year: number | null };
  canMultiSelectCrop: (crop: TimelineCalendarItemsProps<CropItemData>) => boolean;
  handleCropSelection: (cropId: string, isSelected: boolean) => void;
  itemsWithMissingData: ItemsWithMissingData;
  reportingYear: number;
  showBaselineYears: boolean;
  harvestBaselinePeriod?: BaselinePeriod;
  showCropDetails: boolean;
  getCropDetailsPanelLines: (crop: TimelineCalendarItemsProps<CropItemData>) => CropDetailsLine[];
  isSubmissionFeatureEnabled: boolean;
} & ReactCalendarItemRendererProps<TimelineCalendarItemsProps<CropItemData>>): React.ReactNode => {
  const cropReferenceDate = crop.data.cropType === CROP_TYPE_FCPO.MAIN ? crop.end_time : crop.start_time;
  const cropIsWithinReportingPeriod = timeIsWithinReportingPeriod(cropReferenceDate.valueOf(), reportingYear, carbonProgram);
  const fieldBaselineYears = crop.data?.fieldId ? fields.find((field: FieldListField) => field.id === crop.data.fieldId)?.baselineYears ?? [] : [];
  const baselineYearRange = getFieldBaselineYearRange(fieldBaselineYears);

  if (crop?.data) {
    const { key, ...rest } = getItemProps(crop.itemProps ?? {});
    const setCropCardRef = cropCardRefCallback(getCropCardRefMap);
    const isCurrentlySelected = !!selectedCropIds.find((cropId: string) => cropId === crop.id.toString());
    const isSelectableInMultiMode = canMultiSelectCrop(crop);

    const isInMultiSelectionMode = selectedCropIds.length > 0;

    // If in selection mode, show checkboxes for cards matching selection constraints
    // const shouldShowCheckbox =
    //   !isInMultiSelectionMode || (crop.data?.cropType === selectionConstraints.cropType && crop.data?.year === selectionConstraints.year);

    // Determine if the card should be in inactive state during multi selection mode
    const isInactive = isInMultiSelectionMode && !isSelectableInMultiMode && !isCurrentlySelected;

    const isSelectable = isInMultiSelectionMode && isSelectableInMultiMode;

    const hasPendingCropVerification = crop.data?.isFlaggedByVerifier && crop.data?.isMandatory;

    const cropOpenTasksCount = hasPendingCropVerification ? 1 : getCropOpenTasksCount(crop.id.toString(), itemsWithMissingData);

    const cropDetailsPanelLines = getCropDetailsPanelLines(crop);

    const isBaselineCrop =
      harvestBaselinePeriod && crop.data.cropType !== CROP_TYPE_FCPO.MAIN
        ? isSownInBaselinePeriod(crop.start_time, harvestBaselinePeriod)
        : !!crop.data.isBaseline;

    return (
      <div {...rest} key={key} ref={(node) => setCropCardRef(node, crop.group as string, crop.id.toString())}>
        <CropRotationCard
          activePracticeBundles={crop.data?.cropPractices?.activePracticeBundles?.filter((bundle: PracticeBundle) => bundle.active)}
          baselineYearRange={baselineYearRange}
          cropData={crop}
          cropId={crop.id.toString()}
          cropMixId={crop.data?.cropMixId}
          cropName={crop.data?.cropName}
          cropType={crop.data?.cropType}
          currency={carbonProgram?.currency}
          dataTestId={crop.id.toString()}
          enrolledInInventoryProgram={carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory}
          fieldId={crop.data?.fieldId}
          hasResidue={!!crop.data?.hasResidue}
          isBaseline={showBaselineYears && isBaselineCrop}
          isChecked={isCurrentlySelected}
          isConfirmed={!!crop.data?.isConfirmed}
          isEmptyCard={crop.isPlaceholder && !crop.data?.cropId && !crop.data?.recommendations}
          isEmptyWithNoEnrollment={crop.data?.cropName === "" && !crop.id.toString().includes("enrolled")}
          isFlaggedByVerifier={!!crop.data?.isFlaggedByVerifier}
          isInactive={isInactive}
          isMandatory={(crop.data?.cropType === CROP_TYPE_FCPO.MAIN && crop.data?.isMandatory) || isSelectable || hasPendingCropVerification}
          isReportingYear={cropIsWithinReportingPeriod}
          isSelected={itemContext.selected && !isInactive}
          isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
          openTasksCount={cropOpenTasksCount}
          recommendations={crop.data?.recommendations}
          year={crop.end_time.year()}
          // the static panel below (next sibling) already shows this when the "Crop details" toggle is on, so
          // the hover-eye-icon shortcut only applies while that toggle is off
          cropDetailsLines={showCropDetails ? undefined : cropDetailsPanelLines}
        />
        {showCropDetails && cropDetailsPanelLines.length > 0 && <TimelineCropDetailsPanel lines={cropDetailsPanelLines} isInactive={isInactive} />}
      </div>
    );
  }

  return <></>;
};

const GroupRenderer = ({
  carbonProgramOutcomes,
  group,
  setFieldsInView,
  onShowMissingDataYear,
  carbonProgram,
  handleClearFilters,
  hasNoAllotment,
  fieldPayout,
  itemsWithMissingData,
  reportingYear,
  shouldShowPayout,
  onOpenFieldDetails,
}: GroupRendererProps) => {
  const { ref, inView } = useInView({
    delay: 300,
    rootMargin: "48px",
    threshold: 0,
    trackVisibility: true,
    triggerOnce: false,
  });

  useEffect(() => {
    if (group.id === "no-matches") return;

    if (inView) {
      setFieldsInView((prev) => Array.from(new Set([...prev, group.data.id])));
    } else {
      setFieldsInView((prev) => prev.filter((id) => id !== group.data.id));
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  // handle filler group (no matches message)
  if (group.id === "no-matches") {
    return <NoMatchesFeedback handleClearFilters={handleClearFilters} />;
  }

  const fieldOutcomes = (carbonProgramOutcomes as SingleFieldOutcomes)?.fieldOutcomes;
  const fieldOutcomesExist = fieldOutcomes && outcomesExistOnField(fieldOutcomes);

  const outcomesAreValid = !fieldOutcomesExist
    ? false
    : !hasItemsWithMissingData(group.data.itemsWithMissingData) &&
      areFieldOutcomesValidForYear(fieldOutcomes, reportingYear) &&
      isNextYearOutcomeValid(fieldOutcomes, reportingYear, carbonProgram);

  const currentPayout = fieldOutcomesExist ? fieldPayout : null;
  const openTasksCount = getFieldOpenTasksCount(itemsWithMissingData, group.data.verificationEditingRequests);
  const hasFieldOpenTasks = hasFieldLevelVerificationEditingRequests(group.data.id, group.data.verificationEditingRequests);

  const handleMissingDataClick = () => {
    if (hasItemsWithMissingData(group.data.itemsWithMissingData)) {
      const sortedItemsWithMissingDataList = getItemsWithMissingDataSortedByStartTime(group.data.itemsWithMissingData);
      const firstItemWithMissingDataId = sortedItemsWithMissingDataList[0]?.id;
      if (firstItemWithMissingDataId) onShowMissingDataYear(group.data.id, firstItemWithMissingDataId);
    }
  };

  return (
    <>
      {inView ? (
        <div ref={ref} className="width-100" style={{ height: "100%" }}>
          <FieldCell
            areOutcomesValid={outcomesAreValid}
            currentPayout={currentPayout}
            fieldName={group.data.name}
            fieldId={group.data.id}
            hasFieldOpenTasks={hasFieldOpenTasks}
            hasFieldOutcomes={!!fieldOutcomes}
            hasLocation={!!group.data.location}
            hasNoAllotment={hasNoAllotment}
            isEnrolled={group.data.isEnrolled}
            hasNoPayoutDueToNoPoints={!openTasksCount && isFieldWithoutPayoutDueToNoPoints(carbonProgramOutcomes, reportingYear)}
            isRedZone={group.data.isInRedzone}
            openTasksCount={openTasksCount}
            shouldShowPayout={shouldShowPayout}
            size={group.data.size}
            onMissingDataClick={handleMissingDataClick}
            onOpenFieldDetails={() => onOpenFieldDetails(group.data.id)}
          />
        </div>
      ) : (
        <div ref={ref} className="width-100" style={{ height: "125px" }} />
      )}
    </>
  );
};

const defaultMultiSelectionConstraints: { cropType: CROP_TYPE_FCPO | null; year: number | null } = {
  cropType: null,
  year: null,
};

export const AllFieldsTimeline = ({
  allFields,
  filteredFields,
  allFieldsPractices,
  itemsWithMissingDataByFieldId,
  timelineItems,
  carbonProgramOutcomesResponse,
  isLoadingCarbonProgramOutcomes,
  fieldsInView,
  setFieldsInView,
  cropRecommendationsByFieldId,
  farmFieldsCount,
  enrolledFieldsCount,
  enrolledFieldsTotalHectares,
  isEnrollmentDataLoading,
  fieldsSizeSummary,
  showBaselineYears,
  showCropDetails: showCropDetailsProp,
  hasAppliedFilters,
  handleClearFilters,
  onFieldNavigate,
  verificationEditingRequests,
  reportingYear,
  isReportingYearLocked,
  shouldShowIndividualFieldPayouts,
  useMinimumPayoutForFieldCells = false,
  navigateToFirstMissingData = false,
  onNavigateToFirstMissingDataHandled,
  onShowFieldUpload,
}: AllFieldsTimelineProps) => {
  const { t } = useTranslation(["allFieldView", "fieldTimeline"]);
  const { smallDesktop, largeDesktop } = useAppType();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentFarm } = useGetFarms();

  const { cmcMixCropFCPO, showDrawerFromCMC, setCMCMixCropFCPO, setSelectedRecommendationItemOnTimeline, setShowDrawerFromCMC } = useCropMixComposerContext();
  const { isEnabled: isBulkEditingImprovementsEnabled } = useFeatureFlag(FEATURE_FLAGS.BULK_EDITING_IMPROVEMENTS);
  const { mutateAsync: deleteFieldCropPractices } = useBulkDeleteFieldCropPractices({ farmId: currentFarm?.id });

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [showBulkEditDrawer, setShowBulkEditDrawer] = useState<boolean>(false);
  const [bulkDrawerMode, setBulkDrawerMode] = useState<BulkDataDrawerMode>(BULK_DATA_DRAWER_MODE.edit);
  const [selectedCropId, setSelectedCropId] = useState<string | null>(null);
  const [selectedCropType, setSelectedCropType] = useState<CROP_TYPE_FCPO>(CROP_TYPE_FCPO.MAIN);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<TimelineCalendarItemsProps<CropItemData> | null>(null);
  const [multiSelectedItems, setMultiSelectedItems] = useState<Map<string, TimelineCalendarItemsProps<CropItemData>>>(new Map());
  const [fieldIdsToEnroll, setFieldIdsToEnroll] = useState<string[] | null>(null);
  const [showDeleteCropsModal, setShowDeleteCropsModal] = useState<boolean>(false);
  const [fieldsToAddCropsTo, setFieldsToAddCropsTo] = useState<FieldListField[] | null>(null);
  const [cropsToAdd, setCropsToAdd] = useState<BulkEditCrop[] | null>(null);
  // a single eligible field takes the full crop drawer instead of the bulk one, the same branch the table view makes
  const [singleCropToAdd, setSingleCropToAdd] = useState<{ field: FieldListField; crop: CropFCPO; year: number } | null>(null);

  const [multiSelectionConstraints, setMultiSelectionConstraints] = useState<{ cropType: CROP_TYPE_FCPO | null; year: number | null }>(
    defaultMultiSelectionConstraints
  );

  const { scrollRef, getCropCardRefMap, handleShowMissingDataYear, cropCardRefCallback } = useMissingDataNavigation();

  // the checkbox that drives this (TimelineShowOptionsFilter) needs to flip instantly on click — deferring here
  // instead lets every heavy consumer below (row heights, per-crop details panels, grid re-layout) lag behind
  // by a frame rather than blocking that checkbox's own visual update
  const showCropDetails = useDeferredValue(showCropDetailsProp);

  const { data: practiceCrops } = useGetPracticeCrops({});
  const { data: tillagePractices } = useGetPracticeTillages({ enabled: false });
  const { data: organicFertilizerPractices } = useGetPracticeOrganicFertilizers({ enabled: false });
  const { data: mineralFertilizerPractices } = useGetPracticeMineralFertilizers({ enabled: false });

  // computed regardless of the "Crop details" toggle: when it's on, these drive the always-visible panel below
  // the card; when it's off, the same lines back the card's own hover-eye-icon shortcut instead. Only real main
  // crops have anything to show — side crops stay compact, placeholders have nothing recorded yet.
  const getCropDetailsPanelLines = useCallback(
    (crop: TimelineCalendarItemsProps<CropItemData>): CropDetailsLine[] => {
      if (crop.isPlaceholder || crop.data?.cropType !== CROP_TYPE_FCPO.MAIN || !crop.data.cropId) return [];

      return getCropDetailsLines({
        cropPractices: crop.data.cropPractices?.practices ?? [],
        hasResidue: crop.data.hasResidue,
        practiceCrop: practiceCrops?.find((practiceCrop) => practiceCrop.id === crop.data.cropId),
        practiceCatalogs: {
          [PRACTICE_SLUG.TILLAGES]: tillagePractices,
          [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: organicFertilizerPractices,
          [PRACTICE_SLUG.MINERAL_FERTILIZERS]: mineralFertilizerPractices,
        },
        t,
      });
    },
    [practiceCrops, tillagePractices, organicFertilizerPractices, mineralFertilizerPractices, t]
  );

  // `showPayouts` is the same farm-level allotment signal used by the impact panel and field details
  // drawer (`useCarbonProgramImpact`), so field cells never disagree with those surfaces on allotment state.
  const { byField: impactByField, showPayouts } = useCarbonProgramImpact(carbonProgramOutcomesResponse ?? {}, reportingYear);
  const hasNoAllotment = !showPayouts;

  const mapFields = (field: FieldListField): FieldWithEnrollmentData => {
    const practiceBundle = allFieldsPractices?.[field.id]?.availablePracticeBundles;
    const isEnrolled = isFieldEnrolled(practiceBundle, currentFarm?.carbonProgram ?? null);

    return {
      id: field.id,
      title: field.id,
      height: getTimelineRowHeight(showCropDetails),
      data: { ...field, isEnrolled, verificationEditingRequests: verificationEditingRequests?.filter((request) => request.fieldId === field.id) ?? [] },
    };
  };

  // Create a stable reference to track editing requests changes
  const editingRequestsSnapshot = useMemo(() => getVerificationEditingRequestsSnapshot(verificationEditingRequests ?? []), [verificationEditingRequests]);

  // Create a stable snapshot of missing data to prevent unnecessary re-renders
  const missingDataSnapshot = useMemo(() => {
    if (!itemsWithMissingDataByFieldId) return "";

    return Object.entries(itemsWithMissingDataByFieldId)
      .map(([fieldId, data]) => {
        if (!data) return `${fieldId}:empty`;

        const counts = Object.values(data).reduce((acc, curr) => (typeof curr === "boolean" ? acc + (curr ? 1 : 0) : acc + curr.length), 0);
        return `${fieldId}:${counts}`;
      })
      .sort()
      .join("|");
  }, [itemsWithMissingDataByFieldId]);

  const memoizedFields: FieldWithEnrollmentData[] = useMemo(
    () => filteredFields?.map(mapFields),
    [filteredFields, carbonProgramOutcomesResponse, editingRequestsSnapshot, missingDataSnapshot, shouldShowIndividualFieldPayouts, showCropDetails] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const itemsToDisplay = useMemo(() => timelineItems.filter((item) => fieldsInView.includes(item.group as string)), [timelineItems, fieldsInView]);

  // Filter out non-selectable placeholders when in multiselection mode
  const filteredItemsToDisplay = useMemo(() => {
    if (multiSelectedItems.size === 0 || multiSelectionConstraints.cropType === null || multiSelectionConstraints.year === null) return itemsToDisplay;

    const items = Array.from(multiSelectedItems.entries());
    const selectedExistingCrops = items.filter(([_, crop]) => !crop.isPlaceholder).length;

    return itemsToDisplay.filter((item) => {
      const isSelected = multiSelectedItems.has(item.id as string);
      if (isSelected) return true;
      if (selectedExistingCrops > 1 && item.isPlaceholder) return false;
      if (!item.isPlaceholder) return true;
      // Show placeholders that match the current multiselection constraints
      return item.data?.cropType === multiSelectionConstraints.cropType && item.data?.year === multiSelectionConstraints.year;
    });
  }, [itemsToDisplay, multiSelectionConstraints, multiSelectedItems]);

  const fieldsWithMissingDataId = memoizedFields.map((field) => {
    const itemsWithMissingData = itemsWithMissingDataByFieldId[field.id];
    if (!itemsWithMissingData) return field;

    field.data.itemsWithMissingData = itemsWithMissingData;
    return field;
  });

  // Add filler group when no matches are found and search/filters are active
  const groupsWithFiller = useMemo(() => {
    if (!hasAppliedFilters || (hasAppliedFilters && fieldsWithMissingDataId.length > 0)) return fieldsWithMissingDataId;

    const fillerGroup: FieldWithEnrollmentData = {
      id: "no-matches",
      title: "",
      height: 600,
      data: {
        id: "no-matches",
        name: "",
        size: 0,
        isInRedzone: false,
        redzones: [],
        location: { lat: 0, lng: 0 },
        regionId: "",
        baselineYears: [],
        isEnrolled: false,
        itemsWithMissingData: undefined,
        verificationEditingRequests: [],
      },
    };

    return [...fieldsWithMissingDataId, fillerGroup];
  }, [fieldsWithMissingDataId, hasAppliedFilters]);

  // Per-field harvest-to-harvest highlighting only makes sense for harvest-based monitoring periods —
  // calendar-based programs keep the single, farm-wide reporting-year band (verticalLineClassNamesForTime).
  const isHarvestMonitoringPeriod = currentFarm?.carbonProgram?.fieldsManagement.monitoringPeriodType === "harvest";

  const rowHighlights = useMemo(() => {
    // timelineItems is empty both genuinely and while crop-practice data is still loading (e.g. right after
    // a page reload) — skip rendering highlights in that window rather than showing every row's default
    // reporting-period band and then jumping to the real harvest-bounded one once data arrives.
    if (!isHarvestMonitoringPeriod || timelineItems.length === 0) return [];

    return fieldsWithMissingDataId.map((field) => {
      const { isDefaultPeriod, ...period } = getFieldReportingPeriodHighlight(
        field.id.toString(),
        timelineItems,
        reportingYear,
        currentFarm?.carbonProgram ?? null
      );

      return {
        groupId: field.id,
        // The default (no main crop) period spans the program's whole reporting year, which already
        // aligns with the year/month grid dividers — no need for its own border in that case.
        classNames: isDefaultPeriod ? ["current-year", "default-period"] : ["current-year"],
        ...period,
      };
    });
  }, [isHarvestMonitoringPeriod, fieldsWithMissingDataId, timelineItems, reportingYear, currentFarm?.carbonProgram]);

  const baselinePeriodByFieldId = useMemo(() => {
    // same as rowHighlights: wait for the crops, otherwise the harvest-based band jumps once they load
    if (!showBaselineYears || (isHarvestMonitoringPeriod && timelineItems.length === 0)) return {};

    return filteredFields.reduce<Record<string, BaselinePeriod>>((periodByFieldId, field) => {
      const baselinePeriod = getFieldBaselinePeriod(field, timelineItems, isHarvestMonitoringPeriod, currentFarm?.carbonProgram ?? null);
      if (baselinePeriod) periodByFieldId[field.id] = baselinePeriod;
      return periodByFieldId;
    }, {});
  }, [showBaselineYears, isHarvestMonitoringPeriod, filteredFields, timelineItems, currentFarm?.carbonProgram]);

  const baselineRowHighlights = useMemo(
    () =>
      Object.entries(baselinePeriodByFieldId).map(
        ([fieldId, baselinePeriod]): FieldRowHighlight => ({ groupId: fieldId, ...baselinePeriod, classNames: ["baseline-years"] })
      ),
    [baselinePeriodByFieldId]
  );

  const topMissingDataTarget = useMemo((): MissingDataTarget | null => {
    const topGroup = groupsWithFiller.find((group) => {
      if (group.id === "no-matches") return false;
      const firstItem = getItemsWithMissingDataSortedByStartTime(group.data.itemsWithMissingData)[0];
      return !!firstItem?.id;
    });

    if (!topGroup) return null;

    const firstItem = getItemsWithMissingDataSortedByStartTime(topGroup.data.itemsWithMissingData)[0];
    if (!firstItem?.id) return null;

    return {
      fieldId: topGroup.id.toString(),
      itemId: firstItem.id.toString(),
      startTime: firstItem.startTime,
    };
  }, [groupsWithFiller]);

  const ensureFieldInView = useCallback(
    (fieldId: string) => {
      setFieldsInView((prev) => (prev.includes(fieldId) ? prev : [...prev, fieldId]));
    },
    [setFieldsInView]
  );

  const handleNavigationComplete = useCallback(() => {
    onNavigateToFirstMissingDataHandled?.();
  }, [onNavigateToFirstMissingDataHandled]);

  useAutoScrollToMissingData({
    enabled: !!navigateToFirstMissingData,
    target: topMissingDataTarget,
    scrollRef,
    reportingYear,
    getCropCardRefMap,
    handleShowMissingDataYear,
    ensureFieldInView,
    onComplete: handleNavigationComplete,
  });

  const isFetchingHook = !!useIsFetching({ queryKey: [GET_FIELD_PRACTICES] });
  const isMutatingHook = !!useIsMutating();

  const isCMCIconClick = (target: HTMLElement | null) => target?.classList.contains("cmc-plant-icon");

  const getYear = (cropItem: TimelineCalendarItemsProps<CropItemData>): number => {
    console.assert(cropItem.data?.year, "A crop item should always have a year!"); // eslint-disable-line
    return cropItem.data?.year!; // eslint-disable-line
  };

  const canMultiSelectCrop = useCallback(
    (crop: TimelineCalendarItemsProps<CropItemData>): boolean => {
      if (multiSelectedItems.size === 0 && multiSelectionConstraints.cropType === null && multiSelectionConstraints.year === null) {
        return true; // First selection
      }

      // Only allow selecting placeholders if only one existing crop is selected
      // Do not allow selecting more existing crops if one existing crop and at least one placeholder are selected
      const selectedItems = Array.from(multiSelectedItems.entries());
      const selectedExistingCrops = selectedItems.filter(([_, crop]) => !crop.isPlaceholder).length;
      const selectedPlaceholderCrops = selectedItems.filter(([_, crop]) => crop.isPlaceholder).length;
      const isSelected = multiSelectedItems.has(crop.id as string);
      if (isSelected) return true;
      if (selectedExistingCrops > 1 && crop.isPlaceholder) return false;
      if (selectedExistingCrops === 1 && selectedPlaceholderCrops > 0) {
        if (!crop.isPlaceholder) return false;
      }

      return crop.data?.cropType === multiSelectionConstraints.cropType && crop.data?.year === multiSelectionConstraints.year;
    },
    [multiSelectionConstraints.cropType, multiSelectionConstraints.year, multiSelectedItems]
  );

  const handleCropMultiSelection = useCallback(
    (cropId: string, isSelected: boolean) => {
      const timeLineItem = filteredItemsToDisplay.find((item) => item.id === cropId);
      if (!timeLineItem) return;

      if (isSelected) {
        // Add to selection
        if (multiSelectedItems.size === 0) {
          // First selection - set constraints
          setMultiSelectionConstraints({
            cropType: timeLineItem.data?.cropType || defaultMultiSelectionConstraints.cropType,
            year: timeLineItem.data?.year || defaultMultiSelectionConstraints.year,
          });
        }

        if (canMultiSelectCrop(timeLineItem)) {
          setMultiSelectedItems((prev) => new Map(prev).set(cropId, timeLineItem));
        }
      } else {
        // Remove from selection
        setMultiSelectedItems((prev) => {
          const newMap = new Map(prev);
          newMap.delete(cropId);

          // Reset constraints if no crops selected
          if (newMap.size === 0) {
            setMultiSelectionConstraints(defaultMultiSelectionConstraints);
          }
          return newMap;
        });
      }
    },
    [filteredItemsToDisplay, multiSelectedItems, canMultiSelectCrop, setMultiSelectionConstraints, setMultiSelectedItems]
  );

  const handleMultiSelectMode = useCallback(
    (cropId: string, foundCrop: TimelineCalendarItemsProps<CropItemData>, isCheckboxClick: boolean): boolean => {
      const isMultiMode = multiSelectedItems.size > 0;
      const isSelectableInMultiMode = canMultiSelectCrop(foundCrop);

      if (!isMultiMode && isCheckboxClick) {
        const isChecked = multiSelectedItems.has(cropId);
        handleCropMultiSelection(cropId, !isChecked);
        return true;
      }

      if (isMultiMode && isSelectableInMultiMode) {
        const isChecked = multiSelectedItems.has(cropId);
        handleCropMultiSelection(cropId, !isChecked);
        return true;
      }

      if (isMultiMode && !isSelectableInMultiMode) return true;

      return false; // not handled, continue with regular click
    },
    [multiSelectedItems, canMultiSelectCrop, handleCropMultiSelection]
  );

  const handleCardClick = useCallback(
    (cropId: string, event?: React.SyntheticEvent) => {
      const foundCrop = filteredItemsToDisplay.find((field) => field.id === cropId);
      if (!foundCrop) return;
      if (selectedCropId === cropId && (isFetchingHook || isMutatingHook)) return;

      // Handle multi-select mode first
      const target = event?.target as HTMLElement;
      const isCheckbox = isCheckboxClick(target);
      const wasHandledByMultiSelect = handleMultiSelectMode(cropId, foundCrop, isCheckbox);
      if (wasHandledByMultiSelect) return;

      // Extract the base path before /cmc to avoid double /cmc paths
      const basePath = location.pathname.includes("/cmc") ? location.pathname.split("/cmc")[0].replace(/\/$/, "") : location.pathname.replace(/\/$/, "");

      // Continue with a regular click
      if (foundCrop.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION) {
        if (foundCrop.data.cropType === CROP_TYPE_FCPO.UNDERSOWING) return;
        setShowDrawer(false);
        setSelectedCropId(cropId);
        setSelectedCropType(foundCrop.data.cropType);
        setActiveFieldId(foundCrop.group as string);
        setSelectedCrop(foundCrop);
        setSelectedRecommendationItemOnTimeline(foundCrop);
        navigate(`${basePath}/cmc${location.search}`);
        return;
      }

      if (isCMCIconClick(event?.target as HTMLElement) || isCMCIconClick((event?.target as HTMLElement)?.parentElement)) {
        setShowDrawer(false);
        setSelectedCropId(cropId);
        setSelectedCropType(foundCrop.data.cropType);
        setActiveFieldId(foundCrop.group as string);
        setSelectedCrop(foundCrop);
        setSelectedRecommendationItemOnTimeline?.(foundCrop);
        navigate(`${basePath}/cmc/${foundCrop.data.cropMixId}${location.search}`);
        return;
      }

      setSelectedRecommendationItemOnTimeline?.(foundCrop);
      setCMCMixCropFCPO({} as CropFCPO);
      setSelectedCropId(cropId);
      setSelectedCropType(foundCrop.data.cropType);
      setActiveFieldId(foundCrop.group as string);
      setSelectedCrop(foundCrop);
      setShowDrawer(true);
    },
    [
      navigate,
      location,
      isFetchingHook,
      isMutatingHook,
      filteredItemsToDisplay,
      selectedCropId,
      setSelectedRecommendationItemOnTimeline,
      setShowDrawer,
      setSelectedCrop,
      setSelectedCropType,
      setActiveFieldId,
      setCMCMixCropFCPO,
      handleMultiSelectMode,
    ]
  );

  const existingCrops = !activeFieldId ? [] : allFieldsPractices[activeFieldId]?.crops ?? [];

  const getAllCropsWithFieldIdsBasedOnMultiSelection = () => {
    const cropsByFieldId: {
      [fieldId: string]: FieldPracticeResponse;
    } = {};
    const affectedFieldIds = (Array.from(new Set(selectedCropsData.map((crop) => allFields.find((f) => f.id === crop.fieldId)))) as FieldListField[]).map(
      (field) => field.id
    );
    if (!affectedFieldIds) return null;
    for (const id of affectedFieldIds) {
      const value = allFieldsPractices[id];
      if (value) {
        cropsByFieldId[id] = value;
      }
    }
    return cropsByFieldId;
  };

  const existingFertilizers =
    selectedCrop?.data?.cropPractices?.practices?.filter(
      (practice) =>
        (practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) &&
        (selectedCrop?.id.toString().includes("UNKNOWN") || practice.cropId === selectedCrop?.id)
    ) ?? [];

  const existingTillages =
    selectedCrop?.data?.cropPractices?.practices?.filter(
      (practice) => practice.type === PRACTICE_SLUG.TILLAGES && (selectedCrop?.id.toString().includes("UNKNOWN") || practice.cropId === selectedCrop?.id)
    ) ?? [];

  const handleBulkEdit = (mode: BulkDataDrawerMode = BULK_DATA_DRAWER_MODE.edit) => {
    if (multiSelectedItems.size === 0) return;
    setBulkDrawerMode(mode);
    setShowBulkEditDrawer(true);
  };

  const handleClearSelection = () => {
    setMultiSelectedItems(new Map());
    setMultiSelectionConstraints(defaultMultiSelectionConstraints);
  };

  const handleDeleteSelectedCrops = async () => {
    setShowDeleteCropsModal(false);
    try {
      await deleteFieldCropPractices(selectedCropsData.map((crop) => ({ fieldId: crop.fieldId, cropId: crop.id })));
      message({ content: t("fieldTimeline:multiSelection.deleteCropsSuccess"), icon: "tick" });
      handleClearSelection();
    } catch {
      message({ content: t("fieldTimeline:multiSelection.deleteCropsError"), icon: "error" });
    }
  };

  const handleBulkEditFinished = () => {
    setShowBulkEditDrawer(false);
    const hasPlaceholderSelections = [...multiSelectedItems.values()].some((item) => item.isPlaceholder);
    if (hasPlaceholderSelections) {
      setMultiSelectionConstraints(defaultMultiSelectionConstraints);
    }
  };

  // After saving, update the selection to point at the new real crops instead of the old placeholders.
  // Cover/undersowing "add another" placeholders can keep the same id after a real crop is added, so we diff
  // timelineItems for genuinely new crops and match them to selected placeholders by field, crop type, and
  // overlapping date range (rather than relying on the placeholder disappearing).
  const previousTimelineItemIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const previousTimelineItemIds = previousTimelineItemIdsRef.current;
    const newlyAddedCrops = timelineItems.filter((item) => !item.isPlaceholder && !previousTimelineItemIds.has(item.id as string));
    previousTimelineItemIdsRef.current = new Set(timelineItems.map((item) => item.id as string));

    const selectedPlaceholders = [...multiSelectedItems.values()].filter((item) => item.isPlaceholder);
    if (newlyAddedCrops.length === 0 || selectedPlaceholders.length === 0) return;

    const newSelection = new Map(multiSelectedItems);

    for (const placeholder of selectedPlaceholders) {
      const newCrop = newlyAddedCrops.find(
        (crop) =>
          crop.group === placeholder.group &&
          crop.data?.cropType === placeholder.data?.cropType &&
          doRangesOverlap(crop.start_time, crop.end_time, placeholder.start_time, placeholder.end_time) &&
          !newSelection.has(crop.id as string)
      );
      if (newCrop) {
        newSelection.delete(placeholder.id as string);
        newSelection.set(newCrop.id as string, newCrop);
      }
    }

    setMultiSelectedItems(newSelection);

    const firstSelectedCrop = [...newSelection.values()].find((item) => !item.isPlaceholder);
    if (firstSelectedCrop) {
      setMultiSelectionConstraints({ cropType: firstSelectedCrop.data?.cropType ?? null, year: firstSelectedCrop.data?.year ?? null });
    }
  }, [timelineItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get selected crops data for bulk editing
  const getSelectedCropsData = (): { selectedCropsData: BulkEditCrop[]; selectedTillages: PracticeFCPO[]; selectedFertilizers: PracticeFCPO[] } => {
    const selectedCrops: BulkEditCrop[] = [];
    const selectedTillages: PracticeFCPO[] = [];
    const selectedFertilizers: PracticeFCPO[] = [];

    multiSelectedItems.forEach((staleTimelineItem) => {
      const timelineItem = filteredItemsToDisplay.find((item) => item.id === staleTimelineItem.id) ?? staleTimelineItem;
      if (timelineItem?.data) {
        const fieldId = timelineItem.group as string;
        const field = allFields.find((f) => f.id === fieldId);
        const selectedTimelineItems = timelineItem.data?.cropPractices?.crops?.find((crop) => crop.id === timelineItem.id);
        const { cropPractices } = timelineItem.data;
        const availablePracticeBundles = cropPractices?.activePracticeBundles;
        if (field) {
          let cropData: BulkEditCrop;
          const tillages: PracticeFCPO[] = [];
          const fertilizers: PracticeFCPO[] = [];
          if (selectedTimelineItems) {
            // Convert timeline item data to CropFCPO format
            cropData = {
              ...selectedTimelineItems,
              // Add other required fields with defaults
              fieldId,
              isInRedzone: field.isInRedzone,
              isMandatory: timelineItem.data.isMandatory,
              isBaseline: timelineItem.data.isBaseline,
              isPlaceholder: timelineItem.isPlaceholder,
              availablePracticeBundles: availablePracticeBundles || [],
              fertilizerManagement: timelineItem.data.fertilizerManagements,
            };
            const foundTillages = cropPractices?.practices?.filter((practice) => practice.type === "tillages");
            if (foundTillages) {
              tillages.push(...foundTillages);
            }
            const foundFertilizers = cropPractices?.practices?.filter(
              (practice) => practice.type === "mineralFertilizers" || practice.type === "organicFertilizers"
            );
            if (foundFertilizers) {
              fertilizers.push(...foundFertilizers);
            }
          } else {
            cropData = {
              id: timelineItem.id.toString(),
              fieldId: fieldId,
              cropId: timelineItem.data.cropId || "",
              cropName: timelineItem.data.cropName || "",
              cropType: timelineItem.data.cropType,
              year: timelineItem.data.year,
              seedingStartDate: timelineItem.start_time.toISOString(),
              seedingEndDate: "",
              harvestStartDate: "",
              harvestEndDate: timelineItem.end_time.toISOString(),
              hasResidue: timelineItem.data.hasResidue,
              isConfirmed: timelineItem.data.isConfirmed,
              yieldPerHa: timelineItem.data.yieldPerHa,
              yieldPerHaUnit: timelineItem.data.yieldPerHaUnit,
              translationSlug: "",
              // Add other required fields with defaults
              seedingPracticeType: "",
              isMandatory: timelineItem.data.isMandatory,
              isBaseline: timelineItem.data.isBaseline,
              isPlaceholder: timelineItem.isPlaceholder,
              availablePracticeBundles: availablePracticeBundles || [],
              fertilizerManagement: timelineItem.data.fertilizerManagements,
              seedingVerificationStatus: null,
              isInRedzone: field.isInRedzone,
              yieldSoilPlus3DataCollection: timelineItem.data.yieldSoilPlus3DataCollection,
              growingDatesSoilPlus3DataCollection: timelineItem.data.growingDatesSoilPlus3DataCollection,
              seedingSoilPlus3DataCollection: timelineItem.data.seedingSoilPlus3DataCollection,
              fallbackDates: {
                seedingStartDate: timelineItem.start_time.toISOString(),
                harvestEndDate: timelineItem.end_time.toISOString(),
              },
            };
          }

          selectedCrops.push(cropData);
          selectedTillages.push(...tillages);
          selectedFertilizers.push(...fertilizers);
        }
      }
    });

    return { selectedCropsData: selectedCrops, selectedTillages, selectedFertilizers };
  };

  const { selectedCropsData, selectedTillages, selectedFertilizers } = getSelectedCropsData();

  const { isSubmissionFeatureEnabled, isSubmissionPeriod, isDataSubmitted, isDataSubmissionReopened, submissionStatusValue } =
    useSubmissionStatus(reportingYear);

  // The bulk drawer speaks MissingDataForm, the same shape the all-fields data table builds. Deriving it from the
  // table's own helpers (rather than mapping Timeline's items by hand) keeps per-crop practice grouping and the
  // editable flags identical in both views, so a card's "N editable values" means the same thing everywhere.
  const practiceEntriesByCropId = useMemo(() => {
    const entriesByYear = getAllCropsPracticeDataByYear(allFields, currentFarm?.carbonProgram ?? null, allFieldsPractices);
    return new Map(
      Object.values(entriesByYear)
        .flat()
        .map((entry) => [entry.crop.id, entry])
    );
  }, [allFields, currentFarm?.carbonProgram, allFieldsPractices]);

  const bulkDataFormItems = useMemo(
    () =>
      selectedCropsData
        .filter((crop) => !crop.isPlaceholder)
        .map((crop) => {
          // getAllCropsPracticeDataByYear buckets crops by year and drops any it can't date, so a selected crop is
          // not guaranteed to be in there. Rebuild the entry from the crop and its field's practices rather than
          // dropping it, or the drawer would silently edit fewer crops than the farmer selected
          const entry =
            practiceEntriesByCropId.get(crop.id) ??
            ({
              fieldId: crop.fieldId,
              crop,
              hasMissingData: false,
              isTillageDepthRequired: false,
              isEditable: true,
              assignedPractices: {
                [PRACTICE_SLUG.TILLAGES]: selectedTillages.filter((practice) => practice.cropId === crop.id),
                [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: selectedFertilizers.filter(
                  (practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && practice.cropId === crop.id
                ),
                [PRACTICE_SLUG.MINERAL_FERTILIZERS]: selectedFertilizers.filter(
                  (practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS && practice.cropId === crop.id
                ),
                [PRACTICE_SLUG.SEEDINGS]: crop.seedingPracticeType ? [crop.seedingPracticeType] : [],
              },
            } as AllCropsPracticeDataItem);

          return buildMissingDataForm(entry, {
            activeFarmReportingYear: reportingYear,
            isReportingYearLocked,
            selectedYear: multiSelectionConstraints.year ?? entry.crop.year,
            carbonProgram: currentFarm?.carbonProgram ?? null,
            availablePracticeBundles: allFieldsPractices[crop.fieldId]?.availablePracticeBundles ?? [],
            carbonProgramOutcomesForField: carbonProgramOutcomesResponse?.[crop.fieldId],
            isDataSubmitted,
            lastSubmissionDate: submissionStatusValue?.lastSubmissionDate,
            lastSubmissionYear: submissionStatusValue?.lastSubmissionYear,
          });
        }),
    [
      allFieldsPractices,
      carbonProgramOutcomesResponse,
      currentFarm?.carbonProgram,
      isDataSubmitted,
      isReportingYearLocked,
      multiSelectionConstraints.year,
      practiceEntriesByCropId,
      reportingYear,
      selectedCropsData,
      selectedFertilizers,
      selectedTillages,
      submissionStatusValue?.lastSubmissionDate,
      submissionStatusValue?.lastSubmissionYear,
    ]
  );

  // mirrors the data table: the mineral fertilizer card only appears where the program actually offers it, or where
  // the selection already holds mineral data
  const bulkAvailablePracticeTypes = useMemo<BulkDataPointType[]>(() => {
    const hasExistingMineralFertilizerData = bulkDataFormItems.some((item) => (item.mineralFertilizers?.length ?? 0) > 0);
    const showMineralFertilizers = isMineralFertilizerOfferedByProgram(currentFarm?.carbonProgram ?? null) || hasExistingMineralFertilizerData;

    return [
      BULK_CROP_DATA_POINT.cropName,
      PRACTICE_SLUG.CROPS,
      YIELD_PSEUDO_PRACTICE_TYPE,
      PRACTICE_SLUG.SEEDINGS,
      BULK_CROP_DATA_POINT.residue,
      PRACTICE_SLUG.TILLAGES,
      PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      ...(showMineralFertilizers ? [PRACTICE_SLUG.MINERAL_FERTILIZERS] : []),
    ];
  }, [bulkDataFormItems, currentFarm?.carbonProgram]);

  const isPlaceholderOnlySelection = selectedCropsData.length > 0 && selectedCropsData.every((crop) => crop.isPlaceholder);
  // i18next context + count pick the crop-type specific wording, e.g. "main crops to add" / "Add 2 main crops"
  const selectionCropCopyOptions = {
    context: (multiSelectionConstraints.cropType ?? CROP_TYPE_FCPO.MAIN).toLowerCase(),
    count: multiSelectedItems.size,
  };
  const isExistingCropSelection = selectedCropsData.length > 0 && selectedCropsData.every((crop) => !crop.isPlaceholder);

  // 1 real crop + N placeholders selected: its data can be copied onto the placeholders instead of a generic edit
  const selectedPlaceholders = selectedCropsData.filter((crop) => crop.isPlaceholder);
  const singleSelectedRealCrop =
    selectedCropsData.length - selectedPlaceholders.length === 1 ? selectedCropsData.find((crop) => !crop.isPlaceholder) : undefined;
  const isCopyToPlaceholdersSelection = !!singleSelectedRealCrop && selectedPlaceholders.length > 0;

  const showsAddCropsAction = !isCopyToPlaceholdersSelection && isPlaceholderOnlySelection;
  const showsGenericDataActions = !isCopyToPlaceholdersSelection && !isPlaceholderOnlySelection;

  const bulkActionBarSelectionLabel = isCopyToPlaceholdersSelection
    ? t("fieldTimeline:multiSelection.copyingCropToPlaceholders", { cropName: singleSelectedRealCrop!.cropName, count: selectedPlaceholders.length })
    : showsAddCropsAction
    ? t("fieldTimeline:multiSelection.cropsToAdd", selectionCropCopyOptions)
    : undefined;

  const selectedFields = allFields.filter((field) => selectedCropsData.some((crop) => crop.fieldId === field.id));

  // The new drawer edits existing crops only, so any selection carrying an empty slot stays on BulkEditCropDrawer —
  // that includes the "one existing crop + placeholders" combination canMultiSelectCrop deliberately allows, where
  // the existing crop's values seed the crops being created. Routing that here would silently drop the placeholders.
  const hasPlaceholderSelected = selectedCropsData.some((crop) => crop.isPlaceholder);
  const isBulkDataDrawerUsed = isBulkEditingImprovementsEnabled && !hasPlaceholderSelected && bulkDataFormItems.length > 0;

  // the years a crop may be added to, same window the table view offers
  const addCropYearOptions = useMemo(() => {
    const { startYear, endYear } = getAddCropYearWindow(reportingYear, currentFarm?.carbonProgram?.fieldsManagement.monitoringPeriodType);
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
  }, [reportingYear, currentFarm?.carbonProgram?.fieldsManagement.monitoringPeriodType]);

  // the drawer hands back only the fields that can still take the chosen crop type in the chosen year: one of them
  // goes to the full crop drawer, several become empty placeholders for the bulk drawer to fill in
  const handleAddCropTypeContinue = (chosenYear: number, cropType: CROP_TYPE_FCPO, eligibleFields: FieldListField[]) => {
    setFieldsToAddCropsTo(null);

    if (eligibleFields.length === 1) {
      const crop = NEW_CROP(cropType, `${CALENDAR_YEAR_START}${chosenYear}`, `${CALENDAR_YEAR_END}${chosenYear}`);
      setSingleCropToAdd({ field: eligibleFields[0], crop, year: chosenYear });
      return;
    }

    const { getReportingYearRange } = getCurrentReportingValues(reportingYear, currentFarm?.carbonProgram);
    const { start, end } = getReportingYearRange(chosenYear);

    setCropsToAdd(
      eligibleFields.map((field) => ({
        // same date shape the bulk endpoint upserts on: the reporting year range bounds the crop, the inner dates
        // stay open for the drawer to collect
        ...NEW_CROP(cropType, start.toISOString(), end.toISOString()),
        id: generateRandomId(),
        year: chosenYear,
        seedingEndDate: "",
        harvestStartDate: "",
        fieldId: field.id,
        isInRedzone: field.isInRedzone,
        isMandatory: false,
        isBaseline: false,
        isPlaceholder: true,
        availablePracticeBundles: allFieldsPractices[field.id]?.availablePracticeBundles ?? [],
        fertilizerManagement: [],
      }))
    );
  };

  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const onOpenFieldDetails = (fieldId: string) => setSelectedFieldId(fieldId);

  const groupRendererPropsRef = useRef({
    isLoadingCarbonProgramOutcomes,
    currentFarm,
    carbonProgramOutcomesResponse,
    cropRecommendationsByFieldId,
    impactByField,
    useMinimumPayoutForFieldCells,
    setFieldsInView,
    handleShowMissingDataYear,
    handleClearFilters,
    hasNoAllotment,
    itemsWithMissingDataByFieldId,
    onFieldNavigate,
    reportingYear,
    shouldShowIndividualFieldPayouts,
    onOpenFieldDetails,
  });
  groupRendererPropsRef.current = {
    isLoadingCarbonProgramOutcomes,
    currentFarm,
    carbonProgramOutcomesResponse,
    cropRecommendationsByFieldId,
    impactByField,
    useMinimumPayoutForFieldCells,
    setFieldsInView,
    handleShowMissingDataYear,
    handleClearFilters,
    hasNoAllotment,
    itemsWithMissingDataByFieldId,
    onFieldNavigate,
    reportingYear,
    shouldShowIndividualFieldPayouts,
    onOpenFieldDetails,
  };

  const stableGroupRenderer = useCallback(({ group }: { group: FieldWithEnrollmentData; isRightSidebar?: boolean }) => {
    const props = groupRendererPropsRef.current;
    return props.isLoadingCarbonProgramOutcomes ? (
      <Flex align="center" justify="center" style={{ height: "100px" }}>
        <Spin />
      </Flex>
    ) : (
      GroupRenderer({
        carbonProgram: props.currentFarm?.carbonProgram ?? null,
        carbonProgramOutcomes: props.carbonProgramOutcomesResponse?.[group.id] as SingleFieldOutcomes,
        cropRecommendations: props.cropRecommendationsByFieldId?.[group.id] ?? [],
        group,
        setFieldsInView: props.setFieldsInView,
        onShowMissingDataYear: props.handleShowMissingDataYear,
        handleClearFilters: props.handleClearFilters,
        hasNoAllotment: props.hasNoAllotment,
        fieldPayout: props.impactByField[group.id]
          ? props.useMinimumPayoutForFieldCells
            ? props.impactByField[group.id].payoutMin
            : props.impactByField[group.id].payoutAvg
          : null,
        itemsWithMissingData: props.itemsWithMissingDataByFieldId?.[group.id] ?? {},
        onFieldNavigate: props.onFieldNavigate,
        onOpenFieldDetails: props.onOpenFieldDetails,
        reportingYear: props.reportingYear,
        shouldShowPayout: props.shouldShowIndividualFieldPayouts,
      })
    );
  }, []);

  return (
    <>
      <ModalProvider>
        <TimelineCalendarWithFields
          groups={groupsWithFiller}
          allFields={filteredFields}
          onItemClick={(itemId: string, e: React.SyntheticEvent) => handleCardClick(itemId, e)}
          onItemSelect={(itemId: string, e: React.SyntheticEvent) => handleCardClick(itemId, e)}
          // @ts-expect-error nope should be a date type and not a number
          items={filteredItemsToDisplay}
          groupRenderer={stableGroupRenderer}
          itemRenderer={({ item, getItemProps, itemContext, ...props }) =>
            cropRenderer({
              item: item as unknown as TimelineCalendarItemsProps<CropItemData>,
              getItemProps,
              itemContext,
              getCropCardRefMap,
              carbonProgram: currentFarm?.carbonProgram ?? undefined,
              fields: filteredFields,
              cropCardRefCallback,
              selectedCropIds: Array.from(multiSelectedItems.keys()),
              selectionConstraints: multiSelectionConstraints,
              canMultiSelectCrop,
              handleCropSelection: handleCropMultiSelection,
              itemsWithMissingData: itemsWithMissingDataByFieldId?.[item.group as string] ?? {},
              reportingYear,
              showBaselineYears,
              harvestBaselinePeriod: isHarvestMonitoringPeriod ? baselinePeriodByFieldId[item.group as string] : undefined,
              showCropDetails,
              getCropDetailsPanelLines,
              isSubmissionFeatureEnabled,
              ...props,
            })
          }
          highlightCurrentYear={!isHarvestMonitoringPeriod}
          rowHighlights={[...rowHighlights, ...baselineRowHighlights]}
          tagText={t("fieldTimeline:reportingYear")}
          dragSnap={dayInMilliseconds}
          canChangeGroup={false}
          canMove={true}
          canResize={true}
          scrollRef={(el) => (scrollRef.current = el)}
          buffer={10}
          screenSize={largeDesktop ? "large" : smallDesktop ? "small" : "medium"}
          farmFieldsCount={farmFieldsCount}
          enrolledFieldsCount={enrolledFieldsCount}
          enrolledFieldsTotalHectares={enrolledFieldsTotalHectares}
          isEnrollmentDataLoading={isEnrollmentDataLoading}
          fieldsSizeSummary={fieldsSizeSummary}
          onShowFieldUpload={onShowFieldUpload}
          hasAppliedFilters={hasAppliedFilters}
          carbonProgramOutcomesResponse={carbonProgramOutcomesResponse}
          carbonProgram={currentFarm?.carbonProgram}
          reportingYear={reportingYear}
          showCropDetails={showCropDetails}
          // moveResizeValidator={(action, itemId, time, resizeEdge) => moveResizeValidator({ action, item: itemId, time, resizeEdge, currentItems: items })} // 🚨 : I will skip this for now since it is WIP
        />
        {(activeFieldId && showDrawerFromCMC && cmcMixCropFCPO.id && (
          <AddEditCropDrawer
            availablePracticeBundles={allFieldsPractices[activeFieldId]?.availablePracticeBundles ?? []}
            allCrops={existingCrops}
            existingCrop={cmcMixCropFCPO}
            year={cmcMixCropFCPO?.year ?? moment().year()}
            existingFertilizers={existingFertilizers}
            existingTillages={existingTillages}
            field={filteredFields.find((field) => field.id === activeFieldId) ?? filteredFields[0]}
            setShowDrawer={setShowDrawerFromCMC}
            showDrawer={showDrawerFromCMC}
            fertilizerManagements={selectedCrop?.data?.fertilizerManagements ?? []}
            viewType={VIEW_TYPE.ALL_FIELDS}
            farmId={currentFarm?.id}
            carbonProgram={currentFarm?.carbonProgram}
          />
        )) ||
          (activeFieldId && showDrawer && selectedCrop && (
            <AddEditCropDrawer
              availablePracticeBundles={allFieldsPractices[activeFieldId]?.availablePracticeBundles ?? []}
              allCrops={existingCrops}
              existingCrop={
                selectedCrop.data?.cropPractices?.crops?.find((crop) => crop.id === selectedCrop.id) ??
                NEW_CROP(selectedCropType, moment(selectedCrop.start_time).toISOString(), moment(selectedCrop.end_time).toISOString())
              }
              year={getYear(selectedCrop)}
              existingFertilizers={existingFertilizers}
              existingTillages={existingTillages}
              field={filteredFields.find((field) => field.id === activeFieldId) ?? filteredFields[0]}
              setShowDrawer={setShowDrawer}
              showDrawer={showDrawer}
              fertilizerManagements={selectedCrop?.data?.fertilizerManagements ?? []}
              viewType={VIEW_TYPE.ALL_FIELDS}
              formWithOnLoadValidation={
                !selectedCrop.isPlaceholder &&
                ((isSubmissionFeatureEnabled && isSubmissionPeriod) ||
                  (!!itemsWithMissingDataByFieldId[activeFieldId] &&
                    getCropOpenTasksCount(selectedCrop.id.toString(), itemsWithMissingDataByFieldId[activeFieldId]) > 0))
              }
              farmId={currentFarm?.id}
              carbonProgram={currentFarm?.carbonProgram}
            />
          ))}

        {showBulkEditDrawer && isBulkDataDrawerUsed && (
          <BulkDataDrawer
            showDrawer={showBulkEditDrawer}
            missingDataFormItems={bulkDataFormItems}
            selectedYear={multiSelectionConstraints.year ?? bulkDataFormItems[0].year}
            mode={bulkDrawerMode}
            availablePracticeTypes={bulkAvailablePracticeTypes}
            onClose={handleBulkEditFinished}
          />
        )}

        {/* Bulk Edit Crop Drawer — still the path for empty crop slots, which this drawer creates */}
        {showBulkEditDrawer && !isBulkDataDrawerUsed && selectedCropsData.length > 0 && (
          <BulkEditCropDrawer
            selectedCrops={isCopyToPlaceholdersSelection ? selectedPlaceholders : selectedCropsData}
            templateCrop={isCopyToPlaceholdersSelection ? singleSelectedRealCrop : undefined}
            allCrops={getAllCropsWithFieldIdsBasedOnMultiSelection()}
            affectedFields={
              Array.from(
                new Set((isCopyToPlaceholdersSelection ? selectedPlaceholders : selectedCropsData).map((crop) => allFields.find((f) => f.id === crop.fieldId)))
              ) as FieldListField[]
            }
            selectedTillages={selectedTillages}
            selectedFertilizers={selectedFertilizers}
            showDrawer={showBulkEditDrawer}
            setShowDrawer={setShowBulkEditDrawer}
            onFinish={handleBulkEditFinished}
          />
        )}

        {/* Multi-selection floating action bar */}
        {multiSelectedItems.size > 1 &&
          (isBulkEditingImprovementsEnabled ? (
            <BulkActionBar
              selectedCount={multiSelectedItems.size}
              selectedFieldIds={selectedCropsData.map((crop) => crop.fieldId)}
              onClear={handleClearSelection}
              hideSelectionCount={isCopyToPlaceholdersSelection}
              selectionLabel={bulkActionBarSelectionLabel}
              copyLabel={
                isCopyToPlaceholdersSelection ? t("fieldTimeline:multiSelection.copyToPlaceholdersButton", { count: selectedPlaceholders.length }) : undefined
              }
              copyTooltip={isCopyToPlaceholdersSelection ? t("fieldTimeline:multiSelection.copyToPlaceholdersTooltip") : undefined}
              onCopy={isCopyToPlaceholdersSelection ? handleBulkEdit : undefined}
              addCropsLabel={showsAddCropsAction ? t("fieldTimeline:multiSelection.addCropsButton", selectionCropCopyOptions) : undefined}
              cropType={multiSelectionConstraints.cropType ?? CROP_TYPE_FCPO.MAIN}
              onAddCrops={showsAddCropsAction ? handleBulkEdit : showsGenericDataActions ? () => setFieldsToAddCropsTo(selectedFields) : undefined}
              onAddData={showsGenericDataActions ? () => handleBulkEdit(BULK_DATA_DRAWER_MODE.add) : undefined}
              onEditData={showsGenericDataActions ? () => handleBulkEdit(BULK_DATA_DRAWER_MODE.edit) : undefined}
              onEnrollFields={showsGenericDataActions ? setFieldIdsToEnroll : undefined}
              isEnrollFieldsDisabled={isDataSubmitted && !isDataSubmissionReopened}
              // a selection is either empty slots to fill or existing crops to delete — never both
              onDeleteCrops={isExistingCropSelection ? () => setShowDeleteCropsModal(true) : undefined}
            />
          ) : (
            <MultiCropSelectionBar selectedCount={multiSelectedItems.size} onEdit={() => handleBulkEdit()} onClear={handleClearSelection} />
          ))}

        <Modal
          showModal={showDeleteCropsModal}
          onOk={handleDeleteSelectedCrops}
          onCancel={() => setShowDeleteCropsModal(false)}
          okText={t("fieldTimeline:multiSelection.deleteCropsConfirm")}
          cancelText={t("allFieldView:cropForm.cropDeletionModal.cancel")}
          content={
            <>
              <Typography variant="h3">{t("fieldTimeline:multiSelection.deleteCropsTitle")}</Typography>
              <Typography variant="paragraph-m">{t("fieldTimeline:multiSelection.deleteCropsDescription", { count: multiSelectedItems.size })}</Typography>
            </>
          }
        />

        {fieldsToAddCropsTo && (
          <AddCropTypeDrawer
            visible
            onClose={() => setFieldsToAddCropsTo(null)}
            fields={fieldsToAddCropsTo}
            isYearLocked
            fieldPractices={allFieldsPractices}
            year={multiSelectionConstraints.year ?? reportingYear}
            availableYears={addCropYearOptions}
            onContinue={handleAddCropTypeContinue}
          />
        )}

        {/* one eligible field: the crop is filled in through the same drawer the timeline opens for an existing crop */}
        {singleCropToAdd && (
          <AddEditCropDrawer
            availablePracticeBundles={allFieldsPractices[singleCropToAdd.field.id]?.availablePracticeBundles ?? []}
            allCrops={allFieldsPractices[singleCropToAdd.field.id]?.crops ?? []}
            existingCrop={singleCropToAdd.crop}
            year={singleCropToAdd.year}
            existingFertilizers={[]}
            existingTillages={[]}
            field={singleCropToAdd.field}
            showDrawer
            setShowDrawer={(show) => {
              if (!show) {
                setSingleCropToAdd(null);
                handleClearSelection();
              }
            }}
            fertilizerManagements={allFieldsPractices[singleCropToAdd.field.id]?.fertilizerManagements ?? []}
            viewType={VIEW_TYPE.ALL_FIELDS}
            farmId={currentFarm?.id}
            carbonProgram={currentFarm?.carbonProgram}
          />
        )}

        {/* The crops picked above are empty placeholders, so the bulk drawer fills them in like any other selection */}
        {cropsToAdd && cropsToAdd.length > 0 && (
          <BulkEditCropDrawer
            selectedCrops={cropsToAdd}
            allCrops={allFieldsPractices}
            affectedFields={allFields.filter((field) => cropsToAdd.some((crop) => crop.fieldId === field.id))}
            selectedTillages={[]}
            selectedFertilizers={[]}
            showDrawer
            setShowDrawer={(show) => !show && setCropsToAdd(null)}
            onFinish={() => {
              setCropsToAdd(null);
              handleClearSelection();
            }}
          />
        )}

        {/* Enrolment scoped to the parent fields of the current crop selection */}
        {fieldIdsToEnroll && (
          <FieldEnrollmentDrawer
            fields={allFields.filter((field) => fieldIdsToEnroll.includes(field.id))}
            visible={fieldIdsToEnroll.length > 0}
            onClose={() => setFieldIdsToEnroll(null)}
          />
        )}

        {/* Field details drawer — lifted here so GET_FIELDS refetches don't reset its open state */}
        <Drawer
          className="field-details-drawer"
          title={t("farm:editFieldDrawerTitle")}
          visible={!!selectedFieldId}
          onClose={() => setSelectedFieldId(null)}
          styles={{ body: { padding: "0 2rem", display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" } }}
        >
          {selectedFieldId && (
            <FieldDetails
              fieldId={selectedFieldId}
              setShowFieldEditDrawer={(show) => {
                if (!show) setSelectedFieldId(null);
              }}
            />
          )}
        </Drawer>
      </ModalProvider>
    </>
  );
};
